import * as THREE from 'three';
import { SIM_DT } from '../core/Engine.js';
import { clamp } from '../core/Rand.js';
import { MovementConfig } from './MovementConfig.js';
import { PhysicsAdapter } from './PhysicsAdapter.js';
import { Movement } from './Movement.js';
import { CameraRig } from './CameraRig.js';
import { registerPlayerShots } from './PlayerShots.js';
import { createDebugSuite } from './Debug.js';

/**
 * Player — the capsule controller, the movement state machine, the camera rig
 * and the health model.
 *
 * Simulation lives in `fixed(dt)` (locked 120 Hz). Everything cosmetic lives in
 * `update(dt)` and interpolates the simulation with `ctx.time.alpha`, so the
 * view is smooth at any framerate. The main camera is written here and is
 * authoritative; the render team adds screen shake as a transient offset after
 * us and we never fight it.
 *
 * Frozen `ctx.player` API — see CONTRACTS.md. Everything published for the
 * weapons viewmodel is documented at `_publish()`.
 */

const IDLE_INPUT = Object.freeze({
  forward: 0, right: 0,
  jump: false, jumpEdge: false,
  crouch: false, crouchEdge: false,
  sprint: false, lean: 0,
});

export class PlayerSystem {
  name = 'player';
  priority = 50;

  constructor(cfg = MovementConfig) {
    this.cfg = cfg;
    this.adapter = new PhysicsAdapter();
    this.movement = new Movement(cfg, this.adapter);
    this.rig = new CameraRig(cfg, this.movement);
    this.ctx = null;

    this.health = cfg.maxHealth;
    this.maxHealth = cfg.maxHealth;
    this.regenTimer = 0;
    this.dead = false;

    this.frozen = false;
    this._script = null;
    this._lastEdgeFrame = -1;
    this._selfEmit = false;
    this._in = { ...IDLE_INPUT };

    this.movement.onFallDamage = (dmg) => this.applyDamage(dmg, new THREE.Vector3(0, 1, 0), 'fall');
  }

  /* ------------------------------------------------------------------ init */

  init(ctx) {
    this.ctx = ctx;
    this.adapter.init(ctx);
    this.movement.init(ctx);
    this.rig.init(ctx);
    this.rig.yaw = this._spawnYaw();
    this.movement.yaw = this.rig.yaw;

    this.player = {
      /* ---- frozen API (CONTRACTS.md) ---- */
      position: new THREE.Vector3(),          // eye position, world space
      feet: this.movement.feet,               // capsule bottom, world space
      velocity: this.movement.velocity,       // world m/s
      yaw: 0, pitch: 0,                       // radians
      grounded: true, crouched: false, sprinting: false, sliding: false, mantling: false,
      state: 'idle',
      speed: 0,
      health: this.health, maxHealth: this.maxHealth,
      eyeHeight: this.cfg.eyeStand,
      addRecoil: (p, y) => this.rig.addRecoil(p, y),
      applyDamage: (a, d) => this.applyDamage(a, d),
      radius: this.cfg.radius,
      standHeight: this.cfg.standHeight,
      crouchHeight: this.cfg.crouchHeight,

      /* ---- published for the weapons viewmodel (shapes in the report) ---- */
      viewSway: this.rig.viewSway,            // Vector2(yawLagRad, pitchLagRad)
      bobOffset: this.rig.bobOffset,          // Vector3(right, up, forward) metres
      recoilOffset: this.rig.recoilOffset,    // Vector2(pitchRad, yawRad)
      velocityLocal: this.rig.velocityLocal,  // Vector3(right, up, forward) m/s
      mantle: this.movement.mantle,           // see Movement.mantle
      quaternion: this.rig.quaternion,        // final view rotation
      fov: this.cfg.fovBase,
      ads: 0,
      stamina: 1,
      surface: 'concrete',
      airTime: 0,
      leanOffset: 0,
      height: this.cfg.standHeight,
      halfHeight: this.cfg.standHeight * 0.5,
      config: this.cfg,

      /* ---- control surface for other systems / tools ---- */
      teleport: (feet, yaw) => this.teleport(feet, yaw),
      respawn: () => this.respawn(),
      setLook: (yaw, pitch) => { this.rig.yaw = yaw; this.rig.pitch = pitch; this.movement.yaw = yaw; },
      heal: (n) => { this.health = clamp(this.health + n, 0, this.maxHealth); },
      diagnostics: () => ({
        physics: this.adapter.diagnostics,
        counters: this.movement.debugCounters,
      }),
    };
    ctx.player = this.player;
    this.player.debug = createDebugSuite(this);

    ctx.events.on('damage:taken', (p) => {
      if (this._selfEmit) return;
      if (p && p.health === undefined && Number.isFinite(p.amount)) {
        this._applyDamage(p.amount, p.fromDir, 'external', false);
      }
    });

    this._publishSim();
    this.rig.update(0, 1);
    this.rig.applyTo(ctx);
    this._publish();

    registerPlayerShots(this);
  }

  _spawnYaw() {
    /* Face the middle of the level from the spawn, so the default view is
       always looking at something. */
    const lvl = this.ctx?.level;
    const sp = lvl?.spawnPoints?.[0];
    if (!sp) return 0;
    let tx = 0, tz = 0;
    if (lvl.navBounds) { const c = lvl.navBounds.getCenter(new THREE.Vector3()); tx = c.x; tz = c.z; }
    const dx = tx - sp.x, dz = tz - sp.z;
    if (Math.hypot(dx, dz) < 0.5) return 0;
    return Math.atan2(-dx, -dz);
  }

  /* ------------------------------------------------------------------ sim */

  fixed(dt, ctx) {
    if (this.frozen) return;
    this.movement.yaw = this.rig.yaw;
    this.movement.fixed(dt, this._readInput(ctx));
    this._tickHealth(dt);
    this._publishSim();
  }

  update(dt, ctx) {
    if (this.frozen) { this.rig.applyTo(ctx); return; }
    const s = ctx.input?.state;
    if (s && !this._script) this.rig.applyLook(s.dx || 0, s.dy || 0, dt);
    this.rig.leanInput = s?.lean ?? 0;
    this.rig.update(dt, ctx.time?.alpha ?? 1);
    this.rig.applyTo(ctx);
    this._publish();
  }

  _readInput(ctx) {
    if (this._script) return this._script;
    const s = ctx.input?.state;
    const o = this._in;
    if (!s) return Object.assign(o, IDLE_INPUT);
    /* Edge flags are cleared once per rendered frame but fixed() may run
       several times per frame — only the first step sees the edge. */
    const frame = ctx.time?.frame ?? 0;
    const firstStep = frame !== this._lastEdgeFrame;
    this._lastEdgeFrame = frame;
    o.forward = s.forward;
    o.right = s.right;
    o.jump = s.jump;
    o.jumpEdge = firstStep && s.jumpEdge;
    o.crouch = s.crouch;
    o.crouchEdge = firstStep && s.crouchEdge;
    o.sprint = s.sprint;
    o.lean = s.lean;
    return o;
  }

  /* --------------------------------------------------------------- health */

  applyDamage(amount, fromDir, cause = 'hit') {
    return this._applyDamage(amount, fromDir, cause, true);
  }

  _applyDamage(amount, fromDir, cause, emit) {
    if (!Number.isFinite(amount) || amount <= 0) return this.health;
    if (this.dead) return 0;
    this.health = Math.max(0, this.health - amount);
    this.regenTimer = this.cfg.regenDelay;
    const dir = fromDir ? new THREE.Vector3(fromDir.x || 0, fromDir.y || 0, fromDir.z || 0) : new THREE.Vector3(0, 0, 1);
    if (emit) {
      this._selfEmit = true;
      this.ctx?.events?.emit('damage:taken', { amount, fromDir: dir, health: this.health, cause });
      this._selfEmit = false;
    }
    if (this.health <= 0 && !this.dead) {
      this.dead = true;
      this.ctx?.events?.emit('player:death', { position: this.movement.feet.clone(), cause });
    }
    return this.health;
  }

  _tickHealth(dt) {
    if (this.dead) return;
    if (this.regenTimer > 0) { this.regenTimer -= dt; return; }
    if (this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + this.cfg.regenRate * dt);
    }
  }

  respawn() {
    this.health = this.maxHealth;
    this.dead = false;
    this.regenTimer = 0;
    this.movement.resetToSpawn();
    this.rig.reset(this._spawnYaw(), 0);
    this.movement.yaw = this.rig.yaw;
    this._publishSim();
  }

  teleport(feet, yaw) {
    this.movement.teleport(feet, yaw ?? this.rig.yaw);
    this.rig.reset(yaw ?? this.rig.yaw, this.rig.pitch);
    this._publishSim();
  }

  /* -------------------------------------------------------------- publish */

  /** Sim-side values, refreshed every fixed step. */
  _publishSim() {
    const p = this.player;
    const m = this.movement;
    if (!p) return;
    p.yaw = this.rig.yaw;
    p.pitch = this.rig.pitch;
    p.grounded = m.grounded;
    p.crouched = m.crouched;
    p.sprinting = m.sprinting;
    p.sliding = m.sliding;
    p.mantling = m.mantling;
    p.state = m.state;
    p.speed = m.speed;
    p.health = this.health;
    p.stamina = m.stamina;
    p.surface = m.surface;
    p.airTime = m.airTime;
    p.height = m.height;
    p.halfHeight = m.halfHeight;
    p.position.set(m.feet.x, m.feet.y + this.rig.eyeHeight, m.feet.z);
  }

  /** Frame-side values (interpolated). Runs after the camera is composed. */
  _publish() {
    const p = this.player;
    if (!p) return;
    p.position.copy(this.rig.eye);
    p.eyeHeight = this.rig.eyeHeight;
    p.fov = this.rig.fov;
    p.ads = this.rig.adsBlend;
    p.leanOffset = this.rig.leanOffset;
    p.yaw = this.rig.yaw;
    p.pitch = this.rig.pitch;
  }

  /* ------------------------------------------------- scripting for shots */

  /** Freeze the simulation (shots). The camera transform is still written. */
  freeze(on) { this.frozen = !!on; }

  /**
   * Advance the simulation deterministically with scripted input.
   * @param {number} seconds
   * @param {(t:number, i:number)=>object} [inputFn] partial input overrides
   */
  run(seconds, inputFn) {
    const steps = Math.max(0, Math.round(seconds / SIM_DT));
    for (let i = 0; i < steps; i++) {
      this._runStep(inputFn ? inputFn(i * SIM_DT, i) : null);
    }
    this._script = null;
  }

  /** Advance until `pred()` is true (or `maxSeconds` elapses). */
  runUntil(pred, maxSeconds, inputFn) {
    const steps = Math.max(1, Math.round(maxSeconds / SIM_DT));
    for (let i = 0; i < steps; i++) {
      if (pred(i * SIM_DT, i)) { this._script = null; return i * SIM_DT; }
      this._runStep(inputFn ? inputFn(i * SIM_DT, i) : null);
    }
    this._script = null;
    return maxSeconds;
  }

  _runStep(overrides) {
    const s = this._script || (this._script = { ...IDLE_INPUT });
    Object.assign(s, IDLE_INPUT, overrides || null);
    this.movement.yaw = this.rig.yaw;
    this.movement.fixed(SIM_DT, s);
    this._tickHealth(SIM_DT);
    this._publishSim();
    this.rig.leanInput = s.lean;
    this.rig.update(SIM_DT, 1);
    this._publish();
  }

  /** Place the player for a shot / test and clear all cosmetic history. */
  place(feet, yaw = 0, pitch = 0) {
    this.frozen = false;
    this.health = this.maxHealth;
    this.dead = false;
    this.regenTimer = 0;
    this.movement.teleport(feet, yaw);
    this.rig.reset(yaw, pitch);
    this.movement.yaw = yaw;
    this._publishSim();
    this.rig.update(SIM_DT, 1);
    this._publish();
  }

  dispose() { }
}

export function createPlayerSystem(cfg) {
  return new PlayerSystem(cfg);
}

export { MovementConfig };
export default createPlayerSystem;
